export interface Lesson {
  slug: string
  number: number
  title: string
  duration: string
  description: string
  videoUrl: string
  thumbnailUrl: string
  guide?: LessonGuide
}

export interface LessonGuide {
  beforeYouStart?: string[]
  steps: string[]
  success: string
}

export interface Course {
  id: string
  title: string
  description: string
  lessons: Lesson[]
}

export type LearnLocale = 'en' | 'ja' | 'es' | 'ar' | 'ko'

const VIDEO_BASE =
  process.env.NEXT_PUBLIC_LEARN_VIDEO_BASE ??
  'https://reception.martini.film/storage/v1/object/public/public-assets/Learn/20260409'

type LessonContent = Pick<Lesson, 'title' | 'description'>

const GETTING_STARTED_LESSON_GUIDES_EN: Record<string, LessonGuide> = {
  'start-here': {
    beforeYouStart: ['No project setup is required; this introduction is an overview of the full workflow.'],
    steps: [
      'Watch the introduction for the shape of the workflow rather than individual controls.',
      'Open the Learn sidebar when you are ready to move from the overview into a first project.',
    ],
    success: 'You can name the four parts of the Martini loop: generate, review, cut, and export.',
  },
  welcome: {
    beforeYouStart: ['Open a new project in another tab so you can follow the walkthrough on a blank canvas.'],
    steps: [
      'Place a shot and generate one take.',
      'Review the take on the canvas and make one deliberate change.',
      'Drag the selected take to the timeline, trim it, and export an MP4.',
    ],
    success: 'You have a playable export that matches the sequence you reviewed on the timeline.',
  },
  'first-shot': {
    beforeYouStart: ['Have a short prompt ready with a subject, action, framing, and camera movement.'],
    steps: [
      'Press V, place a video shot, and add your prompt.',
      'Choose a model and review the olive estimate before generating.',
      'Play the result with Space and keep the take if its composition and motion work.',
    ],
    success: 'A generated video take is playable on your canvas.',
  },
  iterating: {
    beforeYouStart: ['Start with one generated take whose main problem you can describe clearly.'],
    steps: [
      'Choose one variable to change: prompt, reference, model, duration, or another supported setting.',
      'Generate a new take and compare it with the original.',
      'Repeat only after deciding what improved and what did not.',
    ],
    success: 'You have at least two comparable takes and can explain why you prefer one.',
  },
  organizing: {
    beforeYouStart: ['Add several takes to the canvas so the organization tools have real material to work with.'],
    steps: [
      'Rate promising takes, mark favorites, and reject weak options.',
      'Apply a color label to one useful category.',
      'Create a bin or stack for related work, then filter the canvas to your selects.',
    ],
    success: 'The canvas shows a clear set of selects without deleting the alternatives.',
  },
  'timeline-export': {
    beforeYouStart: ['Keep two or more playable video takes on the canvas.'],
    steps: [
      'Drag the takes onto the timeline and arrange them in story order.',
      'Trim each edge, then play the complete sequence.',
      'Export MP4 for review or an XML package for Premiere Pro or DaVinci Resolve.',
    ],
    success: 'The exported sequence preserves the shot order and trim points you reviewed.',
  },
  collaboration: {
    beforeYouStart: ['Open a project you own and decide whether the other person should edit or only view.'],
    steps: [
      'Open Share and choose edit, view-only, or private access.',
      'Copy the invite link and open it in another signed-in session.',
      'Make a small edit or comment and confirm it appears in both sessions.',
    ],
    success: 'The collaborator has the intended role and live changes appear in both sessions.',
  },
  sets: {
    beforeYouStart: ['Choose a clear environment image and, if needed, references for a character or prop.'],
    steps: [
      'Create a set from the environment image and wait for reconstruction to finish.',
      'Move the virtual camera to a useful composition.',
      'Capture a reference or generate a shot from that camera position.',
    ],
    success: 'You have a new composition from inside the set that preserves the chosen camera angle.',
  },
  upscaling: {
    beforeYouStart: ['Choose a finished video take; upscale after creative iteration, not before it.'],
    steps: [
      'Open the take and choose the upscaling action.',
      'Select the target resolution and any available detail or HDR settings.',
      'Compare the processed result with the source at full size.',
    ],
    success: 'The processed take improves delivery quality without introducing distracting artifacts.',
  },
  'asset-library': {
    beforeYouStart: ['Choose a reusable character, costume, prop, or location from the current project.'],
    steps: [
      'Save the asset to a collection and give it a clear name.',
      'Add a folder, tag, or property that will make it easy to retrieve.',
      'Open another project and reference the saved asset with @.',
    ],
    success: 'The saved asset can be found and reused from another project.',
  },
}

const GETTING_STARTED_LESSON_CONTENT_EN: Record<string, LessonContent> = {
  'start-here': {
    title: 'Welcome to Martini',
    description:
      "Meet Koh, one of Martini's creators, and see how filmmakers use generative media as its own production medium.",
  },
  welcome: {
    title: 'Creating Your First Film',
    description: 'A complete overview of the Martini workflow — from blank canvas to a finished film.',
  },
  'first-shot': {
    title: 'Your First Shot',
    description: 'Generate an image, turn it into video, and learn the basic flow from blank canvas to finished shot.',
  },
  iterating: {
    title: 'Iterating on Shots',
    description:
      'Refine shots with new prompts, references, settings, and models until the result matches your intent.',
  },
  organizing: {
    title: 'Organizing Your Board',
    description: 'Keep the canvas readable with color labels, favorites, rejects, filters, and bins.',
  },
  'timeline-export': {
    title: 'Timeline and Exporting',
    description:
      'Build a rough cut on the timeline, then export an MP4 or XML package for Premiere Pro or DaVinci Resolve.',
  },
  collaboration: {
    title: 'Collaboration',
    description: 'Share projects, work in real time, see collaborator cursors, and cut on the same timeline.',
  },
  sets: {
    title: 'Sets',
    description:
      'Turn one image into a 3D environment, move a virtual camera through it, and generate shots from any angle.',
  },
  upscaling: {
    title: 'Upscaling Your Video',
    description: 'Raise the resolution of a finished shot, sharpen detail, or convert it to HDR.',
  },
  'asset-library': {
    title: 'Asset Libraries',
    description:
      'Save characters, costumes, and locations into collections, organize them with folders, tags, and properties, and reuse them in any project with @.',
  },
}

const GETTING_STARTED_LESSON_CONTENT_JA: Record<string, LessonContent> = {
  'start-here': {
    title: 'Martiniへようこそ',
    description:
      'Martini の開発者の一人である Koh が、フィルムメーカーが生成メディアをひとつの制作手段としてどう使うかを紹介します。',
  },
  welcome: {
    title: 'はじめての映像を作る',
    description: '空のキャンバスから完成した作品まで、Martiniのワークフロー全体を概観します。',
  },
  'first-shot': {
    title: '最初のショット',
    description:
      '画像を生成し、それを動画にしてみましょう。空のキャンバスから完成したショットまでの基本的な流れを学びます。',
  },
  iterating: {
    title: 'ショットを反復する',
    description:
      '新しいプロンプト、参照画像、設定、モデルを使って、意図に合う結果が得られるまでショットを磨き込みます。',
  },
  organizing: {
    title: 'ボードを整理する',
    description: 'カラーラベル、お気に入り、却下、フィルター、ビンを使ってキャンバスを読みやすく保ちます。',
  },
  'timeline-export': {
    title: 'タイムラインと書き出し',
    description:
      'タイムラインでラフカットを組み、Premiere ProまたはDaVinci Resolve向けにMP4またはXMLパッケージを書き出します。',
  },
  collaboration: {
    title: 'コラボレーション',
    description:
      'プロジェクトを共有し、リアルタイムで共同作業し、コラボレーターのカーソルを見ながら同じタイムラインで編集します。',
  },
  sets: {
    title: 'セット',
    description: '1枚の画像から3D環境を生成し、仮想カメラを動かして、任意のアングルからショットを生成できます。',
  },
  upscaling: {
    title: '動画の解像度を上げる',
    description: '完成したショットの解像度を上げて、ディテールをシャープにしたり、HDRに変換したりできます。',
  },
  'asset-library': {
    title: 'アセットライブラリ',
    description:
      'キャラクター・衣装・ロケーションをコレクションとして保存し、フォルダ・タグ・プロパティで整理して、@ でどのプロジェクトでも再利用できます。',
  },
}

const GETTING_STARTED_LESSON_CONTENT_ES: Record<string, LessonContent> = {
  'start-here': {
    title: 'Te damos la bienvenida a Martini',
    description:
      'Conoce a Koh, uno de los creadores de Martini, y descubre cómo los cineastas usan los medios generativos como su propio medio de producción.',
  },
  welcome: {
    title: 'Crea tu primera película',
    description:
      'Un recorrido completo por el flujo de trabajo de Martini, del lienzo en blanco a una película terminada.',
  },
  'first-shot': {
    title: 'Tu primera toma',
    description:
      'Genera una imagen, conviértela en video y aprende el flujo básico, desde un lienzo en blanco hasta la toma final.',
  },
  iterating: {
    title: 'Iterar sobre las tomas',
    description:
      'Pule las tomas con nuevos prompts, referencias, ajustes y modelos hasta que el resultado coincida con tu intención.',
  },
  organizing: {
    title: 'Organizar tu board',
    description: 'Mantén el lienzo legible con etiquetas de color, favoritos, descartes, filtros y bins.',
  },
  'timeline-export': {
    title: 'Línea de tiempo y exportación',
    description:
      'Monta un primer corte en la línea de tiempo y exporta un MP4 o un paquete XML para Premiere Pro o DaVinci Resolve.',
  },
  collaboration: {
    title: 'Colaboración',
    description:
      'Comparte proyectos, trabaja en tiempo real, ve los cursores de tus colaboradores y monta sobre la misma línea de tiempo.',
  },
  sets: {
    title: 'Sets',
    description:
      'Convierte una imagen en un entorno 3D, mueve una cámara virtual por él y genera tomas desde cualquier ángulo.',
  },
  upscaling: {
    title: 'Aumenta la resolución de tu video',
    description: 'Aumenta la resolución de una toma terminada, define los detalles o conviértela a HDR.',
  },
  'asset-library': {
    title: 'Bibliotecas de assets',
    description:
      'Guarda personajes, vestuarios y locaciones en colecciones, organízalas con carpetas, etiquetas y propiedades, y reutilízalas en cualquier proyecto con @.',
  },
}

// Initial LLM-assisted Arabic lesson copy. Koh reviews these before public release.
const GETTING_STARTED_LESSON_CONTENT_AR: Record<string, LessonContent> = {
  'start-here': {
    title: 'مرحبًا بك في Martini',
    description:
      'تعرّف على Koh، أحد مبتكري Martini، وشاهد كيف يستخدم صنّاع الأفلام الوسائط التوليدية كوسيلة إنتاج قائمة بذاتها.',
  },
  welcome: {
    title: 'إنشاء فيلمك الأول',
    description: 'نظرة شاملة على سير العمل في Martini — من الكانفاس الفارغ إلى فيلم مكتمل.',
  },
  'first-shot': {
    title: 'لقطتك الأولى',
    description: 'أنشئ صورة، وحوّلها إلى فيديو، وتعلّم المسار الأساسي من كانفاس فارغ إلى لقطة مكتملة.',
  },
  iterating: {
    title: 'تكرار تحسين اللقطات',
    description: 'حسّن اللقطات بمطالبات ومراجع وإعدادات ونماذج جديدة حتى تطابق النتيجة قصدك.',
  },
  organizing: {
    title: 'تنظيم لوحتك',
    description: 'حافظ على وضوح الكانفاس باستخدام تسميات الألوان، والمفضلات، والمرفوضات، والفلاتر، والحاويات.',
  },
  'timeline-export': {
    title: 'الجدول الزمني والتصدير',
    description:
      'ابنِ مونتاجًا أوليًا على الجدول الزمني، ثم صدّر ملف MP4 أو حزمة XML إلى Premiere Pro أو DaVinci Resolve.',
  },
  collaboration: {
    title: 'التعاون',
    description: 'شارك المشاريع، واعمل في الوقت الفعلي، وشاهد مؤشرات المتعاونين، وحرّروا على الجدول الزمني نفسه.',
  },
  sets: {
    title: 'المجموعات',
    description: 'حوّل صورة واحدة إلى بيئة ثلاثية الأبعاد، وحرّك كاميرا افتراضية داخلها، وأنشئ لقطات من أي زاوية.',
  },
  upscaling: {
    title: 'رفع دقة الفيديو',
    description: 'ارفع دقة لقطة جاهزة، أو زِد حدة التفاصيل، أو حوّلها إلى HDR.',
  },
  'asset-library': {
    title: 'مكتبات الأصول',
    description:
      'احفظ الشخصيات والأزياء والمواقع في تشكيلات، ونظّمها بالمجلدات والوسوم والخصائص، وأعد استخدامها في أي مشروع عبر @.',
  },
}

const GETTING_STARTED_LESSON_CONTENT_KO: Record<string, LessonContent> = {
  'start-here': {
    title: 'Martini에 오신 것을 환영합니다',
    description:
      'Martini를 만든 Koh를 만나고, 필름메이커들이 생성형 미디어를 하나의 제작 수단으로 어떻게 활용하는지 알아보세요.',
  },
  welcome: {
    title: '첫 영상 만들기',
    description: '빈 캔버스에서 완성된 영상까지 Martini 워크플로 전체를 살펴봅니다.',
  },
  'first-shot': {
    title: '첫 샷 만들기',
    description: '이미지를 생성하고 영상으로 바꾸며, 빈 캔버스에서 완성된 샷까지의 기본 흐름을 익힙니다.',
  },
  iterating: {
    title: '샷 반복 개선',
    description: '새 프롬프트, 레퍼런스, 설정, 모델로 결과가 의도에 맞을 때까지 샷을 다듬습니다.',
  },
  organizing: {
    title: '보드 정리하기',
    description: '색상 라벨, 즐겨찾기, 리젝트, 필터, 빈으로 캔버스를 읽기 쉽게 유지합니다.',
  },
  'timeline-export': {
    title: '타임라인과 내보내기',
    description: '타임라인에서 러프 컷을 만든 다음 Premiere Pro 또는 DaVinci Resolve용 MP4나 XML 패키지로 내보냅니다.',
  },
  collaboration: {
    title: '협업',
    description: '프로젝트를 공유하고 실시간으로 함께 작업하며, 협업자의 커서를 보고 같은 타임라인에서 편집합니다.',
  },
  sets: {
    title: '세트',
    description: '이미지 하나를 3D 환경으로 바꾸고 가상 카메라를 움직여 원하는 각도에서 샷을 생성합니다.',
  },
  upscaling: {
    title: '동영상 화질 높이기',
    description: '완성된 샷의 해상도를 높이고 디테일을 선명하게 다듬거나 HDR로 변환하세요.',
  },
  'asset-library': {
    title: '에셋 라이브러리',
    description:
      '캐릭터, 의상, 로케이션을 컬렉션으로 저장하고 폴더·태그·속성으로 정리해 @로 어떤 프로젝트에서든 재사용하세요.',
  },
}

const GETTING_STARTED_COURSE_META: Record<LearnLocale, { title: string; description: string }> = {
  en: {
    title: 'Getting Started',
    description: 'Learn the core workflow: generate shots, organize the canvas, cut a sequence, and export.',
  },
  ja: {
    title: 'はじめに',
    description: '主要なワークフローを学びます: ショット生成、キャンバス整理、シーケンスのカット、書き出し。',
  },
  es: {
    title: 'Primeros pasos',
    description:
      'Aprende el flujo de trabajo central: genera tomas, organiza el lienzo, monta una secuencia y exporta.',
  },
  ar: {
    title: 'البدء',
    description: 'تعلّم سير العمل الأساسي: إنشاء اللقطات، وتنظيم الكانفاس، ومونتاج تسلسل، ثم التصدير.',
  },
  ko: {
    title: '시작하기',
    description: '핵심 워크플로를 익힙니다. 샷 생성, 캔버스 정리, 시퀀스 편집, 내보내기까지 다룹니다.',
  },
}

const LESSON_ORDER: Array<{
  slug: string
  number: number
  duration: string
  videoUrl: string
  thumbnailUrl: string
}> = [
  {
    // The 2:00 introduction film (Koh welcoming new filmmakers). Numbered 0 so
    // the walkthrough lessons keep 1-8; the Tutorials overview features it
    // above the grid and lesson pages label it "Start here" instead of a number.
    slug: 'start-here',
    number: 0,
    duration: '2:00',
    videoUrl: `${VIDEO_BASE}/0-Introduction.mp4`,
    thumbnailUrl: `${VIDEO_BASE}/0-thumb.jpg`,
  },
  {
    slug: 'welcome',
    number: 1,
    // A YouTube walkthrough — `videoUrl` is detected as YouTube and rendered as a
    // native embed (see tutorials/[slug]/page.tsx + lib/youtube). Duration is left
    // blank since the embed shows its own runtime.
    duration: '',
    videoUrl: 'https://youtu.be/6pj2Y0xfA3g',
    thumbnailUrl: 'https://img.youtube.com/vi/6pj2Y0xfA3g/maxresdefault.jpg',
  },
  {
    slug: 'first-shot',
    number: 2,
    duration: '2:30',
    videoUrl: `${VIDEO_BASE}/1-CreatingAShot.mp4`,
    thumbnailUrl: `${VIDEO_BASE}/1-thumb.jpg`,
  },
  {
    slug: 'iterating',
    number: 3,
    duration: '4:00',
    videoUrl: `${VIDEO_BASE}/2-Iterating.mp4`,
    thumbnailUrl: `${VIDEO_BASE}/2-thumb.jpg`,
  },
  {
    slug: 'organizing',
    number: 4,
    duration: '1:00',
    videoUrl: `${VIDEO_BASE}/3-Organizing.mp4`,
    thumbnailUrl: `${VIDEO_BASE}/3-thumb.jpg`,
  },
  {
    slug: 'timeline-export',
    number: 5,
    duration: '1:10',
    videoUrl: `${VIDEO_BASE}/4-timelineAndExport.mp4`,
    thumbnailUrl: `${VIDEO_BASE}/4-thumb.jpg`,
  },
  {
    slug: 'collaboration',
    number: 6,
    duration: '1:00',
    videoUrl: `${VIDEO_BASE}/5-Collaboration.mp4`,
    thumbnailUrl: `${VIDEO_BASE}/5-thumb.jpg`,
  },
  {
    slug: 'sets',
    number: 7,
    duration: '4:20',
    videoUrl: 'https://reception.martini.film/storage/v1/object/public/public-assets/20250503-SetTutorial.mp4',
    thumbnailUrl: `${VIDEO_BASE}/6a-thumb.jpg`,
  },
  {
    slug: 'upscaling',
    number: 8,
    duration: '',
    videoUrl: 'https://youtu.be/nTDx2SQQiLc',
    thumbnailUrl: 'https://img.youtube.com/vi/nTDx2SQQiLc/maxresdefault.jpg',
  },
  {
    slug: 'asset-library',
    number: 9,
    duration: '',
    videoUrl: 'https://youtu.be/5eEg1-vG3yQ',
    thumbnailUrl: 'https://img.youtube.com/vi/5eEg1-vG3yQ/maxresdefault.jpg',
  },
]

const LESSON_CONTENT_BY_LOCALE: Record<LearnLocale, Record<string, LessonContent>> = {
  en: GETTING_STARTED_LESSON_CONTENT_EN,
  ja: GETTING_STARTED_LESSON_CONTENT_JA,
  es: GETTING_STARTED_LESSON_CONTENT_ES,
  ar: GETTING_STARTED_LESSON_CONTENT_AR,
  ko: GETTING_STARTED_LESSON_CONTENT_KO,
}

function buildCourse(locale: LearnLocale): Course {
  const meta = GETTING_STARTED_COURSE_META[locale]
  const content = LESSON_CONTENT_BY_LOCALE[locale]
  return {
    id: 'getting-started',
    title: meta.title,
    description: meta.description,
    lessons: LESSON_ORDER.map(meta => ({
      ...meta,
      title: content[meta.slug].title,
      description: content[meta.slug].description,
      ...(locale === 'en' ? { guide: GETTING_STARTED_LESSON_GUIDES_EN[meta.slug] } : {}),
    })),
  }
}

export const GETTING_STARTED_COURSE: Course = buildCourse('en')

export function getCourse(locale: LearnLocale): Course {
  return buildCourse(locale)
}

export const ALL_LESSONS = GETTING_STARTED_COURSE.lessons

export function getLessons(locale: LearnLocale): Lesson[] {
  return buildCourse(locale).lessons
}

export function getLessonBySlug(slug: string, locale: LearnLocale = 'en'): Lesson | undefined {
  return getLessons(locale).find(l => l.slug === slug)
}

export function getAdjacentLessons(
  slug: string,
  locale: LearnLocale = 'en',
): { prev: Lesson | null; next: Lesson | null } {
  const lessons = getLessons(locale)
  const idx = lessons.findIndex(l => l.slug === slug)
  return {
    prev: idx > 0 ? lessons[idx - 1] : null,
    next: idx < lessons.length - 1 ? lessons[idx + 1] : null,
  }
}

export interface CommunityLink {
  title: string
  author: string
  description: string
  href: string
  thumbnailUrl: string
  meta?: string
}

const COMMUNITY_LINKS_BY_LOCALE: Record<LearnLocale, CommunityLink[]> = {
  en: [
    {
      title: 'Step into Set',
      author: 'Theoretically Media',
      description: 'Full walkthrough of the Martini workflow — generating shots, iterating, and exporting.',
      meta: 'from 9:36',
      href: 'https://youtu.be/MOkjfFIIb6E?si=gBL2bpWoQeLsZU9E&t=576',
      thumbnailUrl: 'https://reception.martini.film/storage/v1/object/public/public-assets/Learn/theo-1.jpg',
    },
    {
      title: 'AI-Powered Automotive CGI',
      author: 'Caio Haar',
      description:
        'Cinematic car ads built with Martini and Seedance 2.0 — exploring CGI + AI pipelines for automotive content.',
      href: 'https://www.linkedin.com/posts/caio-haar-3d_madewithmartini-automotivevisualization-realtimerendering-ugcPost-7454965686935744512-1DpF',
      thumbnailUrl: 'https://reception.martini.film/storage/v1/object/public/public-assets/Learn/ciao-1.jpg',
    },
    {
      title: "Don't Buy an AI Film Studio. Build Yours.",
      author: 'Theoretically Media',
      description: 'Why piecing together your own AI filmmaking pipeline in Martini beats buying an all-in-one studio.',
      href: 'https://www.youtube.com/watch?v=aTqMKA6OQ_M',
      thumbnailUrl: 'https://img.youtube.com/vi/aTqMKA6OQ_M/maxresdefault.jpg',
    },
    {
      title: 'We Tested Martini',
      author: 'AI Filmmaking Academy',
      description: 'A hands-on review of Martini as a platform for collaborative AI filmmaking.',
      href: 'https://www.youtube.com/watch?v=8tbWyc_Pu5o',
      thumbnailUrl: 'https://img.youtube.com/vi/8tbWyc_Pu5o/maxresdefault.jpg',
    },
  ],
  ja: [
    {
      title: 'Step into Set',
      author: 'Theoretically Media',
      description: 'Martiniのワークフロー全体を解説 — ショット生成、反復、書き出しまで。',
      meta: '9:36から',
      href: 'https://youtu.be/MOkjfFIIb6E?si=gBL2bpWoQeLsZU9E&t=576',
      thumbnailUrl: 'https://reception.martini.film/storage/v1/object/public/public-assets/Learn/theo-1.jpg',
    },
    {
      title: 'AIによる自動車CGI',
      author: 'Caio Haar',
      description:
        'MartiniとSeedance 2.0で制作した映画的なカーCM — 自動車コンテンツにおけるCGI+AIパイプラインを探求しています。',
      href: 'https://www.linkedin.com/posts/caio-haar-3d_madewithmartini-automotivevisualization-realtimerendering-ugcPost-7454965686935744512-1DpF',
      thumbnailUrl: 'https://reception.martini.film/storage/v1/object/public/public-assets/Learn/ciao-1.jpg',
    },
    {
      title: 'AI映画スタジオは買うな。自分で作れ。',
      author: 'Theoretically Media',
      description: 'オールインワンのスタジオを買うより、Martiniで自分のAI制作パイプラインを組む方が優れている理由。',
      href: 'https://www.youtube.com/watch?v=aTqMKA6OQ_M',
      thumbnailUrl: 'https://img.youtube.com/vi/aTqMKA6OQ_M/maxresdefault.jpg',
    },
    {
      title: 'Martiniを試してみた',
      author: 'AI Filmmaking Academy',
      description: '共同制作のためのAI映画制作プラットフォームとして、Martiniを実際に試したレビュー。',
      href: 'https://www.youtube.com/watch?v=8tbWyc_Pu5o',
      thumbnailUrl: 'https://img.youtube.com/vi/8tbWyc_Pu5o/maxresdefault.jpg',
    },
  ],
  es: [
    {
      title: 'Step into Set',
      author: 'Theoretically Media',
      description: 'Recorrido completo del flujo de trabajo de Martini — generar tomas, iterar y exportar.',
      meta: 'desde 9:36',
      href: 'https://youtu.be/MOkjfFIIb6E?si=gBL2bpWoQeLsZU9E&t=576',
      thumbnailUrl: 'https://reception.martini.film/storage/v1/object/public/public-assets/Learn/theo-1.jpg',
    },
    {
      title: 'CGI automotriz con IA',
      author: 'Caio Haar',
      description:
        'Anuncios de coches cinematográficos hechos con Martini y Seedance 2.0 — explorando pipelines de CGI + IA para contenido automotriz.',
      href: 'https://www.linkedin.com/posts/caio-haar-3d_madewithmartini-automotivevisualization-realtimerendering-ugcPost-7454965686935744512-1DpF',
      thumbnailUrl: 'https://reception.martini.film/storage/v1/object/public/public-assets/Learn/ciao-1.jpg',
    },
    {
      title: 'No compres un estudio de cine con IA. Crea el tuyo.',
      author: 'Theoretically Media',
      description:
        'Por qué armar tu propio pipeline de cine con IA en Martini supera a comprar un estudio todo en uno.',
      href: 'https://www.youtube.com/watch?v=aTqMKA6OQ_M',
      thumbnailUrl: 'https://img.youtube.com/vi/aTqMKA6OQ_M/maxresdefault.jpg',
    },
    {
      title: 'Probamos Martini',
      author: 'AI Filmmaking Academy',
      description: 'Una reseña práctica de Martini como plataforma de cine con IA para la colaboración.',
      href: 'https://www.youtube.com/watch?v=8tbWyc_Pu5o',
      thumbnailUrl: 'https://img.youtube.com/vi/8tbWyc_Pu5o/maxresdefault.jpg',
    },
  ],
  ar: [
    {
      title: 'الدخول إلى المجموعة',
      author: 'Theoretically Media',
      description: 'شرح كامل لسير عمل Martini — إنشاء اللقطات، والتكرار، والتصدير.',
      meta: 'من 9:36',
      href: 'https://youtu.be/MOkjfFIIb6E?si=gBL2bpWoQeLsZU9E&t=576',
      thumbnailUrl: 'https://reception.martini.film/storage/v1/object/public/public-assets/Learn/theo-1.jpg',
    },
    {
      title: 'CGI للسيارات بالذكاء الاصطناعي',
      author: 'Caio Haar',
      description:
        'إعلانات سيارات سينمائية أُنشئت باستخدام Martini وSeedance 2.0 — استكشاف مسارات عمل CGI + AI لمحتوى السيارات.',
      href: 'https://www.linkedin.com/posts/caio-haar-3d_madewithmartini-automotivevisualization-realtimerendering-ugcPost-7454965686935744512-1DpF',
      thumbnailUrl: 'https://reception.martini.film/storage/v1/object/public/public-assets/Learn/ciao-1.jpg',
    },
    {
      title: 'لا تشترِ استوديو أفلام بالذكاء الاصطناعي. ابنِ استوديوك.',
      author: 'Theoretically Media',
      description: 'لماذا يتفوق بناء خط إنتاج أفلامك بالذكاء الاصطناعي في Martini على شراء استوديو متكامل جاهز.',
      href: 'https://www.youtube.com/watch?v=aTqMKA6OQ_M',
      thumbnailUrl: 'https://img.youtube.com/vi/aTqMKA6OQ_M/maxresdefault.jpg',
    },
    {
      title: 'جرّبنا Martini',
      author: 'AI Filmmaking Academy',
      description: 'مراجعة عملية لـ Martini كمنصة لصناعة الأفلام بالذكاء الاصطناعي للعمل التعاوني.',
      href: 'https://www.youtube.com/watch?v=8tbWyc_Pu5o',
      thumbnailUrl: 'https://img.youtube.com/vi/8tbWyc_Pu5o/maxresdefault.jpg',
    },
  ],
  ko: [
    {
      title: '세트 안으로 들어가기',
      author: 'Theoretically Media',
      description: 'Martini 워크플로 전체 둘러보기. 샷 생성, 반복 개선, 내보내기까지 다룹니다.',
      meta: '9:36부터',
      href: 'https://youtu.be/MOkjfFIIb6E?si=gBL2bpWoQeLsZU9E&t=576',
      thumbnailUrl: 'https://reception.martini.film/storage/v1/object/public/public-assets/Learn/theo-1.jpg',
    },
    {
      title: 'AI 기반 자동차 CGI',
      author: 'Caio Haar',
      description:
        'Martini와 Seedance 2.0으로 만든 시네마틱 자동차 광고. 자동차 콘텐츠를 위한 CGI + AI 파이프라인을 살펴봅니다.',
      href: 'https://www.linkedin.com/posts/caio-haar-3d_madewithmartini-automotivevisualization-realtimerendering-ugcPost-7454965686935744512-1DpF',
      thumbnailUrl: 'https://reception.martini.film/storage/v1/object/public/public-assets/Learn/ciao-1.jpg',
    },
    {
      title: 'AI 영화 스튜디오를 사지 마세요. 직접 만드세요.',
      author: 'Theoretically Media',
      description:
        '올인원 스튜디오를 사는 것보다 Martini로 나만의 AI 영화 제작 파이프라인을 구성하는 것이 더 나은 이유.',
      href: 'https://www.youtube.com/watch?v=aTqMKA6OQ_M',
      thumbnailUrl: 'https://img.youtube.com/vi/aTqMKA6OQ_M/maxresdefault.jpg',
    },
    {
      title: 'Martini를 직접 사용해봤습니다',
      author: 'AI Filmmaking Academy',
      description: '협업형 AI 영화 제작 플랫폼으로서 Martini를 직접 사용해본 리뷰.',
      href: 'https://www.youtube.com/watch?v=8tbWyc_Pu5o',
      thumbnailUrl: 'https://img.youtube.com/vi/8tbWyc_Pu5o/maxresdefault.jpg',
    },
  ],
}

export const COMMUNITY_LINKS: CommunityLink[] = COMMUNITY_LINKS_BY_LOCALE.en

export function getCommunityLinks(locale: LearnLocale): CommunityLink[] {
  return COMMUNITY_LINKS_BY_LOCALE[locale]
}
